import { useCallback, useState } from "react"

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Skeleton } from "@/components/ui/skeleton"

type RuleCategory = "skill" | "trait" | "block_die" | "action" | "injury"

interface RuleEntry {
  name: string
  description: string
  meta: Record<string, string>
}

interface RulePopoverProps {
  category: RuleCategory
  ruleKey: string
  children: React.ReactNode
}

const cache = new Map<string, RuleEntry>()

/** @internal Test-only: clears the rule cache. */
export function clearRuleCache() {
  cache.clear()
}

export function RulePopover({ category, ruleKey, children }: RulePopoverProps) {
  const [open, setOpen] = useState(false)
  const [rule, setRule] = useState<RuleEntry | null>(null)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)

  const cacheKey = `${category}/${ruleKey}`

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen)

      if (!nextOpen) return

      const cached = cache.get(cacheKey)
      if (cached) {
        setRule(cached)
        setError(false)
        return
      }

      setLoading(true)
      setError(false)
      setRule(null)

      fetch(
        `/rules/${encodeURIComponent(category)}/${encodeURIComponent(ruleKey)}`,
      )
        .then((res) => {
          if (!res.ok) throw new Error("Not found")
          return res.json() as Promise<RuleEntry>
        })
        .then((data) => {
          cache.set(cacheKey, data)
          setRule(data)
        })
        .catch(() => setError(true))
        .finally(() => setLoading(false))
    },
    [category, ruleKey, cacheKey],
  )

  const metaLine = rule?.meta
    ? Object.values(rule.meta).filter(Boolean).join(" \u00b7 ")
    : null

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
        {children}
      </PopoverTrigger>
      <PopoverContent
        className="w-72 border-[var(--border-heavy)] bg-[var(--card)] p-3 text-[var(--foreground)]"
        sideOffset={6}
        onClick={(e) => e.stopPropagation()}
      >
        {loading && (
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        )}
        {error && (
          <p className="text-muted-foreground text-xs">
            Rule description not available.
          </p>
        )}
        {rule && !loading && (
          <>
            <div className="font-display mb-1 text-sm font-bold tracking-wide text-[var(--accent)] uppercase">
              {rule.name}
            </div>
            {metaLine && (
              <div className="text-muted-foreground mb-2 text-[10px] font-semibold tracking-widest uppercase">
                {metaLine}
              </div>
            )}
            <p className="text-muted-foreground text-xs leading-relaxed">
              {rule.description}
            </p>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}
